import {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { Box, MenuItem } from '@mui/material';
import { Header as SharedHeader } from '../../shared/components/header';
import { DepartmentSelector } from '../departmentSelector';
import { PageName } from '../../shared/types/util/pageName';
import { EquipmentOwnerSelector } from '../equipmentOwnerSelector';
import { Company } from '../company';
import { stopPropagation } from '../../shared/logic/functions';
import { SearchSelect } from '../../shared/components/searchSelect';
import { SelectItem } from '../../shared/types/util/selectItem';
import { useGetCompanyListQuery, useGetMyCompanyQuery, useSetMyCompanyMutation } from '../../shared/redux/rental';
import { useDispatch } from '../../shared/hooks/redux';
import { resetFilters as resetLeftFilters } from '../../core/redux/leftFilterState';
import { resetFilters as resetViewFilters } from '../../core/redux/viewSetting';

export const Header: FC<{
  page?: PageName,
  children?: ReactNode
}> = ({
  page,
  children,
}) => {
  const { data: currentCompany, isFetching } = useGetMyCompanyQuery();
  const { data: companies } = useGetCompanyListQuery();
  const [setCompanyMutation] = useSetMyCompanyMutation();
  const dispatch = useDispatch();
  const closeMenuRef = useRef<(() => void) | undefined>(undefined);

  const setCompany = useCallback((item: SelectItem | undefined) => {
    if (item) {
      setCompanyMutation(String(item.id));
      dispatch(resetLeftFilters());
      dispatch(resetViewFilters());
      closeMenuRef.current?.();
    }
  }, [dispatch, setCompanyMutation]);

  const companyItems: SelectItem[] = useMemo(
    () => (companies ?? []).map((c) => ({ id: c, label: c })),
    [companies],
  );
  const extraMenuItems = useMemo(() => (
    <MenuItem
      onClick={stopPropagation}
      onKeyDown={stopPropagation}
      sx={{ paddingTop: 1 }}
      disableRipple
      disableTouchRipple
    >
      <SearchSelect
        label="Selskap"
        value={currentCompany ? { id: currentCompany.name, label: currentCompany.name } : undefined}
        onChange={setCompany}
        loading={isFetching}
        size="small"
      >
        {companyItems}
      </SearchSelect>
    </MenuItem>
  ), [currentCompany, setCompany, companyItems, isFetching]);
  return (
    <SharedHeader
      page={page}
      company={currentCompany}
      extraMenuItems={extraMenuItems}
      onCloseMenuReady={(closeMenu) => { closeMenuRef.current = closeMenu; }}
      middleContent={(
        <Box flex={1} display="flex" alignItems="center" minWidth={250} maxWidth={900} gap={4} paddingRight={2}>
          <DepartmentSelector />
          <EquipmentOwnerSelector />
        </Box>
      )}
      rightMiddleContent={(
        <Box flex={1} display="flex" alignItems="center" gap={4} paddingRight={2} sx={{ placeContent: 'end' }}>
          <Company />
        </Box>
      )}
    >
      {children}
    </SharedHeader>
  );
};
